import React from "react";
// BASE Components
import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";
// Material UI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
// Custom Components
import EMentor from "../components/EMentor";
import MCInstructions from "../components/MCInstructions";
import ComponentCard from "../components/Component";
import ComponentPreview from "../components/Preview";
import ComponentStepLabel from "../components/StepLabel";
import SubComponentCard from "../components/SubComponent";

export default function ConceptCategorizing(props) {
  const { data, dir, ins = [], course, comments = [] } = props;
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  if (!data) {
    return <BASELoading />;
  }

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };

  // determine question type
  const questionType = (type) => {
    switch (type) {
      case 0: {
        return "Observation vs Inference";
      }
      case 1: {
        return "Cause vs Effect";
      }
      case 2: {
        return "Supporting Point vs. Counterpoint";
      }
      case 3: {
        return "Valid Cause/Effect vs. Invalid Cause/Effect";
      }
    }
  };

  const questionInstructions = (ins, type) => {
    const pickInstructions = (key) => {
      return ins.filter((data) => data.label.includes(`-${key}`));
    };

    switch (type) {
      case 0: {
        return pickInstructions("default");
      }
      case 1: {
        return pickInstructions("variation-0");
      }
      case 2: {
        return pickInstructions("variation-1");
      }
      case 3: {
        return pickInstructions("variation-2");
      }
    }
  };

  return (
    <>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
          }
        `}
      </style>

      {data.core.map((question, index) => (
        <ComponentCard
          key={question.id}
          title={"Question " + (index + 1)}
          id={question.id}
          commentsCount={componentCommentsCount(question.id)}
        >
          <SubComponentCard
            title="Instructions"
            id={question.id + "instructions"}
            commentsCount={componentCommentsCount(question.id + "instructions")}
          >
            <MCInstructions
              ins={questionInstructions(ins, question.type_id)}
              course={course}
              dir={dir}
              tl={tl}
              componentCommentsCount={componentCommentsCount}
              qIndex={index}
            />
          </SubComponentCard>
          <SubComponentCard
            title="Authentic Material"
            id={question.id + "material"}
            commentsCount={componentCommentsCount(question.id + "material")}
          >
            <Paper elevation={0} square className="p-16">
              {question.materials.map((material, num) => (
                <Grid key={num} container spacing={1}>
                  <Grid item xs={12} sm={4} lg={2}>
                    <Typography className="text-14 font-semibold m-0">
                      Title (TL):
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} lg={10}>
                    <Typography className={`${tl} m-0`} dir={dir}>
                      {material.material_title_tl}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={2}>
                    <Typography className="text-14 font-semibold m-0">
                      Title (EN):
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} lg={10}>
                    <Typography className="text-14 m-0">
                      {material.material_title_en}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} lg={2}>
                    <Typography className="text-14 font-semibold m-0">
                      Source URL:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} lg={10}>
                    <Typography className="text-14 m-0">
                      {material.source_url}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Paper>
          </SubComponentCard>

          <SubComponentCard
            title="Content"
            id={question.id + "content"}
            commentsCount={componentCommentsCount(question.id + "content")}
          >
            <Paper elevation={0} square className="p-16 mb-16">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        Prompt:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className={`${tl} m-0`} dir={dir}>
                        {question.prompt}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
            <Paper elevation={0} variant="outlined" square className="p-16">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container spacing={0}>
                    <Grid item xs={12}>
                      <Typography className="text-14 font-bold m-0">
                        Lures:
                      </Typography>
                      <Divider className="my-16" />
                    </Grid>
                    {question.lures.map((lure, index) => (
                      <Grid
                        key={`lure-${lure.lure_text}-${index}`}
                        item
                        xs={12}
                      >
                        {index > 0 && <Divider className="mt-2 mb-8" />}
                        <Grid container spacing={0} columns={24}>
                          <Grid item xs={4}>
                            {lure.is_correct ? (
                              <Grid container direction="row">
                                <Typography className="text-14 font-semibold m-0">
                                  Correct
                                </Typography>
                                <BASESvgIcon
                                  className="text-green-600 mx-2"
                                  size={20}
                                >
                                  heroicons-solid:check-circle
                                </BASESvgIcon>
                                <Typography className="text-14 font-semibold m-0">
                                  Lure:
                                </Typography>
                              </Grid>
                            ) : (
                              <Grid container direction="row">
                                <Typography className="text-14 font-semibold m-0">
                                  Incorrect
                                </Typography>
                                <BASESvgIcon
                                  className="text-red-600 mx-2"
                                  size={20}
                                >
                                  heroicons-solid:x-circle
                                </BASESvgIcon>
                                <Typography className="text-14 font-semibold m-0">
                                  Lure:
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                          <Grid item xs>
                            <Typography
                              className={`${tl} m-0 ${
                                index < question.lures.length - 1 && `mb-8`
                              }`}
                              dir={dir}
                            >
                              {lure.lure_text}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container spacing={0} columns={24}>
                          <Grid item xs={4}>
                            <Typography className="text-14 font-semibold m-0 mt-2">
                              Feedback:
                            </Typography>
                          </Grid>
                          <Grid item xs>
                            <Typography className={`${tl} m-0 mt-2`} dir={dir}>
                              {lure.lure_feedback}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </SubComponentCard>
        </ComponentCard>
      ))}
      {data.eMentor.length !== 0 && (
        <EMentor
          eMentor={data.eMentor}
          tl={tl}
          dir={dir}
          componentCommentsCount={componentCommentsCount}
        />
      )}
    </>
  );
}
